import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch } from '../lib/api';
const POLL_INTERVAL_MS = 5000;
export function useProjectDetail(projectId) {
    const [detail, setDetail] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isActing, setIsActing] = useState(false);
    const mountedRef = useRef(true);
    const basePath = `/api/orchestrator/projects/${projectId}`;
    const fetchAll = useCallback(async () => {
        try {
            const [detailRes, tasksRes, eventsRes] = await Promise.all([
                apiFetch(basePath),
                apiFetch(`${basePath}/tasks`),
                apiFetch(`${basePath}/events`),
            ]);
            if (mountedRef.current) {
                setDetail(detailRes);
                setTasks(tasksRes.tasks || []);
                setEvents(eventsRes.events || []);
                setError(null);
            }
        }
        catch (e) {
            if (mountedRef.current) {
                setError(e instanceof Error ? e.message : 'Failed to load project');
            }
        }
        finally {
            if (mountedRef.current) {
                setIsLoading(false);
            }
        }
    }, [basePath]);
    // Initial fetch + polling
    useEffect(() => {
        mountedRef.current = true;
        setIsLoading(true);
        fetchAll();
        const interval = setInterval(fetchAll, POLL_INTERVAL_MS);
        return () => {
            mountedRef.current = false;
            clearInterval(interval);
        };
    }, [fetchAll]);
    const performAction = useCallback(async (actionFn) => {
        setIsActing(true);
        try {
            await actionFn();
            await fetchAll();
        }
        finally {
            if (mountedRef.current) {
                setIsActing(false);
            }
        }
    }, [fetchAll]);
    const actions = {
        start: () => performAction(() => apiFetch(`${basePath}/start`, { method: 'POST' })),
        pause: () => performAction(() => apiFetch(`${basePath}/pause`, { method: 'POST' })),
        resume: () => performAction(() => apiFetch(`${basePath}/resume`, { method: 'POST' })),
        cancel: () => performAction(() => apiFetch(`${basePath}/cancel`, { method: 'POST' })),
        approve: () => performAction(() => apiFetch(`${basePath}/approve`, { method: 'POST' })),
        requestChanges: (feedback) => performAction(() => apiFetch(`${basePath}/request-changes`, {
            method: 'POST',
            body: JSON.stringify({ feedback }),
        })),
        kick: () => performAction(() => apiFetch(`${basePath}/kick`, { method: 'POST' })),
        deleteProject: () => performAction(() => apiFetch(basePath, { method: 'DELETE' })),
        retryTask: (taskId) => performAction(() => apiFetch(`${basePath}/tasks/${taskId}/retry`, { method: 'POST' })),
    };
    return { detail, tasks, events, isLoading, error, actions, isActing };
}
